import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import { useCallback, useEffect, useState } from 'react';
import {
  useWorkspaceDirectoryBootstrap,
  withPlacements,
} from '@/lib/assistants/use-directory-bootstrap';
import { onCloudLinkChanged } from '@/lib/cloud-link';
import { syncedWorktree } from '@/lib/desktop-shell/conversation-worktree';
import type {
  StateSetter,
  SyncedAgentConfig,
  SyncedThread,
  SyncedWorkspace,
} from '@/lib/desktop-shell/store';
import { errorMessage } from '@/lib/error-message';
import { listLocalConversations, listLocalWorkspaces } from '@/lib/local-conversations';
import { loadCloudConversations, withCloudEntries } from './cloud-conversations';

type SyncBootstrapStatus = 'loading' | 'ready' | 'error';

type SyncBootstrapData = {
  machines?: { id: string; platform: string }[];
  logicalWorkspaces?: { id: string; name: string; syncKey: string | null }[];
  threads: Omit<SyncedThread, 'runtime'>[];
  workspaces: SyncedWorkspace[];
  agentConfigs: SyncedAgentConfig[];
  pullRequests: Record<string, PullRequestSummary | null>;
  removedWorkspacePaths: string[];
};

const LOCAL_ONLY_SYNC_BOOTSTRAP: SyncBootstrapData = {
  machines: [],
  logicalWorkspaces: [],
  threads: [],
  workspaces: [],
  agentConfigs: [],
  pullRequests: {},
  removedWorkspacePaths: [],
};

export function loadWorkspaceSyncBootstrap(
  native: boolean,
  loadRemote: () => Promise<SyncBootstrapData> = () =>
    apiFetch<SyncBootstrapData>('/api/sync/bootstrap', { credentials: 'include' }),
): Promise<SyncBootstrapData> {
  return native ? Promise.resolve(LOCAL_ONLY_SYNC_BOOTSTRAP) : loadRemote();
}

/** Keeps the first snapshot for an id; bootstrap puts this machine's rows first. */
export function localFirstUniqueById<T extends { id: string }>(entries: readonly T[]): T[] {
  const unique = new Map<string, T>();
  for (const entry of entries) {
    if (!unique.has(entry.id)) unique.set(entry.id, entry);
  }
  return [...unique.values()];
}

export interface WorkspaceSyncBootstrapOptions {
  native: boolean | null;
  preview: boolean;
  reconcileRemovedProjects: (
    workspacePaths: readonly string[],
    removedWorkspacePaths: readonly string[],
  ) => void;
  setSyncedThreads: StateSetter<readonly SyncedThread[]>;
  setSyncedWorkspaces: StateSetter<readonly SyncedWorkspace[]>;
  setSyncedAgentConfigs: StateSetter<readonly SyncedAgentConfig[]>;
  setSyncedPullRequests: StateSetter<Readonly<Record<string, PullRequestSummary | null>>>;
  setWorkspaceSyncError: (error: string | null) => void;
}

/** Loads cloud-owned sidebar state, then merges this machine's conversations from the local server. */
export function useWorkspaceSyncBootstrap({
  native,
  preview,
  reconcileRemovedProjects,
  setSyncedThreads,
  setSyncedWorkspaces,
  setSyncedAgentConfigs,
  setSyncedPullRequests,
  setWorkspaceSyncError,
}: WorkspaceSyncBootstrapOptions): {
  status: SyncBootstrapStatus;
  /** Loads the conversations again (Retry, after they failed to sync). */
  retry: () => void;
} {
  const [status, setStatus] = useState<SyncBootstrapStatus>(preview ? 'ready' : 'loading');
  const [attempt, setAttempt] = useState(0);

  // Workspaces and projects load with the shell; the threads take their placements.
  useWorkspaceDirectoryBootstrap({
    preview,
    onPlacements: useCallback(
      (placements) => setSyncedThreads((current) => withPlacements(current, placements)),
      [setSyncedThreads],
    ),
  });

  useEffect(() => {
    if (native === null || preview) return;
    let cancelled = false;
    let retryTimer: number | null = null;
    setStatus('loading');
    void loadWorkspaceSyncBootstrap(native)
      .then(async (data) => {
        if (cancelled) return;
        const [localConversations, storedLocalWorkspaces] =
          native === true
            ? await Promise.all([listLocalConversations(), listLocalWorkspaces()])
            : [[], []];
        if (cancelled) return;
        const remoteMachineIds = new Set(
          (data.machines ?? [])
            .filter((machine) => machine.platform === 'cloud')
            .map((machine) => machine.id),
        );
        const logicalWorkspaces = new Map(
          (data.logicalWorkspaces ?? []).map((workspace) => [workspace.id, workspace] as const),
        );
        // Server workspace rows are cloud execution locations only. Device paths
        // come from this machine's local server.
        const workspaces: SyncedWorkspace[] = data.workspaces
          .filter((entry) => remoteMachineIds.has(entry.machineId ?? ''))
          .map((entry) => {
            const logical =
              entry.logicalWorkspaceId === undefined
                ? undefined
                : logicalWorkspaces.get(entry.logicalWorkspaceId);
            return {
              ...entry,
              registered: true,
              runtime: 'cloud' as const,
              ...(logical === undefined ? {} : { name: logical.name, syncKey: logical.syncKey }),
              availableOnDevice: true,
            };
          });
        const localWorkspaceByRoot = new Map(
          storedLocalWorkspaces.map(
            (entry) =>
              [
                entry.rootPath,
                {
                  ...entry,
                  registered: true as const,
                  availableOnDevice: entry.availableOnDevice !== false,
                } satisfies SyncedWorkspace,
              ] as const,
          ),
        );
        const canonicalWorkspaceIdByConversation = new Map<string, string>();
        const conversationWorkspaces: SyncedWorkspace[] = localConversations.map((conversation) => {
          const associated = localWorkspaceByRoot.get(conversation.workspaceRoot);
          const id = associated?.id ?? conversation.workspaceId;
          canonicalWorkspaceIdByConversation.set(conversation.id, id);
          return {
            ...associated,
            id,
            logicalWorkspaceId:
              associated?.logicalWorkspaceId ?? conversation.logicalWorkspaceId ?? id,
            availableOnDevice: conversation.workspaceAvailable !== false,
            runtime: conversation.runtime,
            rootPath: conversation.workspaceRoot,
            name: conversation.workspaceName,
            sourceRoot:
              associated?.sourceRoot ??
              (conversation.worktree?.path === conversation.workspaceRoot
                ? conversation.worktree.sourceRoot
                : null),
          };
        });
        const localWorkspaces = localFirstUniqueById([
          ...localWorkspaceByRoot.values(),
          ...conversationWorkspaces,
        ]);
        const localThreads: SyncedThread[] = localConversations.map((conversation) => ({
          id: conversation.id,
          runtime: conversation.runtime,
          workspaceId:
            canonicalWorkspaceIdByConversation.get(conversation.id) ?? conversation.workspaceId,
          workspaceRoot: conversation.workspaceRoot,
          worktree: syncedWorktree(conversation.worktree),
          agentConfigId: conversation.agentConfigId,
          title: conversation.title,
          collaboration: conversation.collaboration,
          modelId: conversation.modelId,
          updatedAt: conversation.updatedAt,
          lastMessageAt: conversation.lastMessageAt,
          messageRevision: conversation.messageRevision,
          tags: conversation.tags,
          pullRequest: conversation.pullRequest ?? null,
          pullRequestBindings: conversation.pullRequestBindings ?? [],
          ...(conversation.placement === undefined ? {} : { placement: conversation.placement }),
        }));
        const localAgentConfigs: SyncedAgentConfig[] = localConversations.map((conversation) => ({
          id: conversation.agentConfigId,
          registryId: conversation.agent.id,
          command: conversation.agent.command,
        }));
        setSyncedThreads(
          localFirstUniqueById([
            ...localThreads,
            // The account snapshot never says where a thread runs: what it lists is the
            // cloud's, this device's own threads coming from its local server above (they
            // win on an id both list).
            ...data.threads.map((thread) => ({ ...thread, runtime: 'cloud' as const })),
          ]).sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt)),
        );
        setSyncedWorkspaces(localFirstUniqueById([...localWorkspaces, ...workspaces]));
        setSyncedAgentConfigs(localFirstUniqueById([...localAgentConfigs, ...data.agentConfigs]));
        setSyncedPullRequests(data.pullRequests);
        reconcileRemovedProjects(
          [...localWorkspaces, ...workspaces]
            .filter((entry) => entry.availableOnDevice)
            .map((entry) => entry.rootPath),
          data.removedWorkspacePaths.filter((path) => !localWorkspaceByRoot.has(path)),
        );
        setWorkspaceSyncError(null);
        setStatus('ready');
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        if (attempt < 2) {
          retryTimer = window.setTimeout(
            () => setAttempt((current) => current + 1),
            300 * 2 ** attempt,
          );
          return;
        }
        setStatus('error');
        setWorkspaceSyncError(errorMessage(reason));
      });
    return () => {
      cancelled = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [
    attempt,
    native,
    preview,
    reconcileRemovedProjects,
    setSyncedAgentConfigs,
    setSyncedPullRequests,
    setSyncedThreads,
    setSyncedWorkspaces,
    setWorkspaceSyncError,
  ]);

  // The conversations on Crowdex Cloud join the sidebar once this server's own
  // are listed, and again whenever the cloud is linked or unlinked; an
  // unreachable cloud only misses its own.
  const [cloudLinkRevision, setCloudLinkRevision] = useState(0);
  useEffect(() => onCloudLinkChanged(() => setCloudLinkRevision((value) => value + 1)), []);
  // biome-ignore lint/correctness/useExhaustiveDependencies(cloudLinkRevision): the cloud was linked or unlinked: its conversations are listed again.
  useEffect(() => {
    if (status !== 'ready' || preview) return;
    let cancelled = false;
    void loadCloudConversations()
      .then((merged) => {
        if (cancelled) return;
        setSyncedThreads((current) =>
          withCloudEntries(current, merged.threads).sort(
            (left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt),
          ),
        );
        setSyncedWorkspaces((current) => withCloudEntries(current, merged.workspaces));
        setSyncedAgentConfigs((current) =>
          localFirstUniqueById([...current, ...merged.agentConfigs]),
        );
        setSyncedPullRequests((current) => ({ ...merged.pullRequests, ...current }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [
    status,
    preview,
    cloudLinkRevision,
    setSyncedAgentConfigs,
    setSyncedPullRequests,
    setSyncedThreads,
    setSyncedWorkspaces,
  ]);

  const retry = useCallback(() => {
    setWorkspaceSyncError(null);
    setAttempt((current) => current + 1);
  }, [setWorkspaceSyncError]);
  return { status, retry };
}
